import {
  Card,
  Title1,
  Text,
  Badge,
  Input,
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableCellLayout,
  makeStyles,
  tokens,
  Button,
  Tooltip,
} from '@fluentui/react-components';
import {
  ArrowSync24Regular,
  Warning24Regular,
  SearchRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import { RowLink, ROW_LINK_HOST } from '../components/links/EntityLink';
import { useBookkeepingClients, useTaskSyncFreshness, BookkeepingRecordWithFilteredTasks } from '../hooks/useClients';
import { applyDrill, useProductivityDrill } from '../components/productivity/productivityDrill';
import { DrillBanner } from '../components/productivity/DrillBanner';
import {
  FilterColumn, SavedView, useListView, QuickViewPills, ActiveFiltersChips, ListColumnHeader,
} from '../components/listFilters';
import { formatCalendarDate } from '../utils/calendarDate';
import { StatusBadge } from '../components/common/StatusBadge';
import { PageSkeleton } from '../components/common/PageSkeleton';
import { SyncStalenessBanner } from '../components/common/SyncStalenessBanner';
import i18n, { useTranslation } from '../i18n';
import { taskWindowLabel, sinceStartOfYear } from '../utils/taskWindow';

const useStyles = makeStyles({
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    padding: tokens.spacingVerticalM,
  },
  pills: { marginRight: 'auto' },
  search: { minWidth: '240px' },
});

type Client = BookkeepingRecordWithFilteredTasks;

const clientName = (c: Client) =>
  c['_cr8f7_client_value@OData.Community.Display.V1.FormattedValue'] || c.cr8f7_name || i18n.t('common.unknown');
const complexityOf = (txns: number) => (txns > 300 ? 'high' : txns > 100 ? 'medium' : 'low');

// Column catalog: drives the header filters, the client-side filter engine, and sort.
//
// Labels, info and options are GETTERS (Spanish UI), as on the Reviews grid: this catalog is
// module-level, and every surface that reads `.label` — headers, filter panels, chips — must read it
// in the language of that moment, including after a toggle on an open page.
const tl = (key: string) => i18n.t(key);
const option = (value: string, labelKey: string) => ({ value, get label() { return tl(labelKey); } });
const columns: FilterColumn<Client>[] = [
  {
    key: 'client', get label() { return tl('clients.col.client'); }, sortKey: 'client', filterType: 'text', getValue: clientName,
    get info() { return tl('clients.col.clientInfo'); },
  },
  {
    // C86: a staff lookup, so one searchable multi-select (its options are the bookkeepers in the loaded list). No owner is
    // NO VALUE, the "(has no value)" row, never the translated word "Unassigned" (a URL would then mean different things
    // in English and Spanish).
    key: 'bookkeeper', get label() { return tl('clients.col.bookkeeper'); }, sortKey: 'bookkeeper', filterType: 'picklist',
    getValue: (c) => c['_ownerid_value@OData.Community.Display.V1.FormattedValue'] || null,
    get info() { return tl('clients.col.bookkeeperInfo'); },
  },
  {
    key: 'status', get label() { return tl('clients.col.status'); }, sortKey: 'status', filterType: 'picklist',
    filterOptions: [
      option('not-started', 'clients.statusOption.notStarted'),
      option('in-progress', 'clients.statusOption.inProgress'),
      option('waiting', 'clients.statusOption.waiting'),
      option('completed', 'clients.statusOption.completed'),
    ],
    getValue: (c) => c.statusCategory,
    get info() { return tl('clients.col.statusInfo'); },
  },
  {
    // Column KEY stays 'tasks' for URL/query-grammar stability; only the label changed.
    key: 'tasks', get label() { return tl('clients.col.tasks'); }, sortKey: 'tasks', filterType: 'number',
    getValue: (c) => c.filteredOutstandingTasks || 0,
    get info() { return tl('clients.col.tasksInfo'); },
  },
  {
    key: 'txns', get label() { return tl('clients.col.txns'); }, sortKey: 'txns', filterType: 'number',
    getValue: (c) => c.cr8f7_avgmonthlytransactions || 0,
    get info() { return tl('clients.col.txnsInfo'); },
  },
  {
    key: 'complexity', get label() { return tl('clients.col.complexity'); }, sortKey: 'txns', filterType: 'picklist',
    filterOptions: [
      option('low', 'complexity.label.low'), option('medium', 'complexity.label.medium'), option('high', 'complexity.label.high'),
    ],
    getValue: (c) => complexityOf(c.cr8f7_avgmonthlytransactions || 0),
    get info() { return tl('clients.col.complexityInfo'); },
  },
  {
    key: 'lockDate', get label() { return tl('clients.col.lockDate'); }, sortKey: 'lockDate', filterType: 'date-range',
    getValue: (c) => c.cr8f7_transactionlockdatexero ?? null,
    get info() { return tl('clients.col.lockDateInfo'); },
  },
];

const views: SavedView[] = [
  { key: 'all', get label() { return tl('clients.views.all'); }, query: {} },
  { key: 'in-progress', get label() { return tl('clients.views.inProgress'); }, query: { 'filter.status.eq': 'in-progress' } },
  { key: 'waiting', get label() { return tl('clients.views.waiting'); }, query: { 'filter.status.eq': 'waiting' } },
  { key: 'completed', get label() { return tl('clients.views.completed'); }, query: { 'filter.status.eq': 'completed' } },
];

export default function ClientsPage() {
  const styles = useStyles();
  const { t } = useTranslation();
  const { data: clients, isLoading, error, refetch } = useBookkeepingClients();
  // Same cache entry as the list above — no extra request. Renders only when the mirror is behind.
  const { data: syncFreshness } = useTaskSyncFreshness();
  // The window's first DAY comes from the API and is worded here (src/utils/taskWindow).
  const taskPeriodLabel = taskWindowLabel(clients?.[0]?.taskCountSince, clients?.[0]?.taskCountPeriod, sinceStartOfYear());

  const list = useListView<Client>({
    defaultViewKey: 'all',
    views,
    columns,
    data: clients,
    searchAccessors: [
      clientName,
      (c) => c['_ownerid_value@OData.Community.Display.V1.FormattedValue'],
      (c) => c.cr8f7_name,
    ],
  });
  // C86: a lookup column's header gets its facet counts and (for the staff column) its options from the loaded list.
  const lookupProps = (c: FilterColumn<Client>) => ({
    getFacetCounts: () => list.facetCountsFor(c.key),
    getLookupOptions: () => list.lookupOptionsFor(c.key),
  });

  // The productivity chart's gray segment opens here (spec §5): the clients with a month no review
  // covers. Rows are bookkeeping records, so a client with two records shows twice; the banner
  // counts CLIENTS, which is what the segment names.
  const { state: drillState, clear: clearDrill } = useProductivityDrill();
  const rows = applyDrill(list.processedData, drillState, (c) => c._cr8f7_client_value);
  const shownClients = new Set(rows.map((c) => (c._cr8f7_client_value ?? '').toLowerCase())).size;

  if (isLoading) {
    return <PageSkeleton variant="rows" />;
  }

  if (error) {
    return (
      <Card style={{ padding: '24px', textAlign: 'center' }}>
        <Warning24Regular style={{ fontSize: '48px', color: tokens.colorPaletteRedForeground1 }} />
        <Title1 style={{ marginTop: '16px' }}>{t('clients.errorTitle')}</Title1>
        <Text style={{ color: tokens.colorNeutralForeground3 }}>
          {error instanceof Error ? error.message : t('clients.errorFallback')}
        </Text>
        <Button
          appearance="primary"
          icon={<ArrowSync24Regular />}
          onClick={() => refetch()}
          style={{ marginTop: '16px' }}
        >
          {t('clients.retry')}
        </Button>
      </Card>
    );
  }

  return (
    <div>
      <SyncStalenessBanner freshness={syncFreshness} />
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title1>{t('clients.title')}</Title1>
            <Text style={{ color: tokens.colorNeutralForeground3 }}>
              {t('clients.count', { shown: drillState.kind === 'none' ? list.filteredCount : rows.length, total: list.totalCount })}
            </Text>
          </div>
          <Button
            appearance="subtle"
            icon={<ArrowSync24Regular />}
            onClick={() => refetch()}
          >
            {t('clients.refresh')}
          </Button>
        </div>
      </div>

      {/* Quick-view pills + search */}
      <Card style={{ marginBottom: '16px' }}>
        <div className={styles.controls}>
          <div className={styles.pills}>
            <QuickViewPills views={views} activeKey={list.activeViewKey} onSelect={list.selectView} />
          </div>
          <Input
            className={styles.search}
            placeholder={t('clients.search')}
            value={list.search}
            onChange={(_, d) => list.setSearch(d.value)}
            contentBefore={<SearchRegular />}
            contentAfter={
              list.search ? (
                <Button appearance="transparent" icon={<DismissRegular />} size="small"
                  onClick={() => list.setSearch('')} aria-label={t('clients.clearSearch')} />
              ) : undefined
            }
          />
        </div>
      </Card>

      <ActiveFiltersChips columns={columns} value={list.filters} onChange={list.setFilters} />

      <DrillBanner state={drillState} shown={shownClients} noun="client" onClear={clearDrill} />

      {/* Clients Table */}
      <Card className="client-table">
        <Table>
          <TableHeader>
            <TableRow>
              <ListColumnHeader column={columns[0]} sort={list.sort} onSortChange={list.setSort} {...lookupProps(columns[0])}
                filters={list.filters} onFiltersChange={list.setFilters} style={{ width: '220px' }} />
              <ListColumnHeader column={columns[1]} sort={list.sort} onSortChange={list.setSort} {...lookupProps(columns[1])}
                filters={list.filters} onFiltersChange={list.setFilters} style={{ width: '140px' }} />
              <ListColumnHeader column={columns[2]} sort={list.sort} onSortChange={list.setSort} {...lookupProps(columns[2])}
                filters={list.filters} onFiltersChange={list.setFilters} style={{ width: '130px' }} />
              <ListColumnHeader column={columns[3]} sort={list.sort} onSortChange={list.setSort} {...lookupProps(columns[3])}
                filters={list.filters} onFiltersChange={list.setFilters} style={{ width: '100px' }} />
              <ListColumnHeader column={columns[4]} sort={list.sort} onSortChange={list.setSort} {...lookupProps(columns[4])}
                filters={list.filters} onFiltersChange={list.setFilters} style={{ width: '100px' }} />
              <ListColumnHeader column={columns[5]} sort={list.sort} onSortChange={list.setSort} {...lookupProps(columns[5])}
                filters={list.filters} onFiltersChange={list.setFilters} style={{ width: '100px' }} />
              <ListColumnHeader column={columns[6]} sort={list.sort} onSortChange={list.setSort} {...lookupProps(columns[6])}
                filters={list.filters} onFiltersChange={list.setFilters} style={{ width: '120px' }} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(client => {
              const txns = client.cr8f7_avgmonthlytransactions || 0;
              const complexity = complexityOf(txns);
              const lockDate = client.cr8f7_transactionlockdatexero
                ? formatCalendarDate(client.cr8f7_transactionlockdatexero)
                : '-';
              const outstandingTasks = client.filteredOutstandingTasks || 0;
              const completedTasks = client.filteredCompletedTasks || 0;

              return (
                <TableRow key={client.cr8f7_bookkeepingid} className={ROW_LINK_HOST} style={{ cursor: 'pointer' }}>
                  <TableCell>
                    <TableCellLayout>
                      {/* Real links (fleet ruling 2026-10-29): the whole row is this link, so right-click opens the client in a new tab. */}
                      <RowLink href={`/clients/${client.cr8f7_bookkeepingid}`}>
                        <Text weight="semibold">{clientName(client)}</Text>
                      </RowLink>
                      <br />
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                        {client.cr8f7_name}
                      </Text>
                    </TableCellLayout>
                  </TableCell>
                  <TableCell>
                    {client['_ownerid_value@OData.Community.Display.V1.FormattedValue'] || t('common.unassigned')}
                  </TableCell>
                  <TableCell>
                    <StatusBadge category={client.statusCategory} />
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      content={t('clients.tasksTooltip', { period: taskPeriodLabel.toLowerCase(), completed: completedTasks })}
                      relationship="label"
                    >
                      <Text size={200}>
                        <span style={{ color: outstandingTasks > 0 ? tokens.colorPaletteRedForeground1 : tokens.colorNeutralForeground3 }}>
                          {outstandingTasks}
                        </span>
                      </Text>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {txns.toLocaleString(i18n.language)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      appearance="filled"
                      color={
                        complexity === 'high'
                          ? 'danger'
                          : complexity === 'medium'
                          ? 'warning'
                          : 'success'
                      }
                    >
                      {t(`complexity.label.${complexity}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Text size={200} style={{
                      color: lockDate === '-' ? tokens.colorNeutralForeground3 : tokens.colorNeutralForeground1
                    }}>
                      {lockDate}
                    </Text>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {rows.length === 0 && (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <Text style={{ color: tokens.colorNeutralForeground3 }}>
              {t('clients.empty')}
            </Text>
          </div>
        )}
      </Card>
    </div>
  );
}
